import React, { useState, useMemo } from 'react';
import { Table, Card, Modal, Carousel, Form, Button } from 'react-bootstrap';
import { FaExclamationTriangle, FaMapMarkerAlt, FaStickyNote, FaClipboardList, FaChevronDown, FaChevronRight, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import './StackCleaningTable.css';

/**
 * StackCleaningTable - Displays kitchen stack cleaning data grouped by stack
 * Each stack is a collapsible row that expands to show unit details
 */
function StackCleaningTable({
  unitsData,
  sortedData,
  hoveredUnitId,
  onHoverUnit,
  hoveredStackId,
  onHoverStack,
  searchTerm,
  externalSelectedUnit,
  onExternalSelectedUnitClear,
  onExpandedStacksChange,
}) {
  // Track which stacks are expanded
  const [expandedStacks, setExpandedStacks] = useState({});
  // Track stacks manually collapsed during search (to override auto-expand)
  const [manuallyCollapsed, setManuallyCollapsed] = useState({});
  // Sort state for stack table
  const [sortColumn, setSortColumn] = useState('stack'); // 'stack' or 'health'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Modal state for photo carousel
  const [showModal, setShowModal] = useState(false);
  const [carouselPhotos, setCarouselPhotos] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselTitle, setCarouselTitle] = useState('');

  // Modal state for View Report
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Handle external unit selection (from building visualization click)
  React.useEffect(() => {
    if (externalSelectedUnit) {
      setSelectedRow(externalSelectedUnit);
      setShowReportModal(true);
      // Clear the external selection after opening the modal
      if (onExternalSelectedUnitClear) {
        onExternalSelectedUnitClear();
      }
    }
  }, [externalSelectedUnit, onExternalSelectedUnitClear]);

  // Helper to extract floor number from unit number
  // 3 digits: first digit is floor (e.g., 501 = floor 5)
  // 4 digits: first 2 digits are floor (e.g., 1001 = floor 10)
  // Handles special cases like "PH7" (penthouse)
  const getFloorFromUnit = (unitNumber) => {
    if (!unitNumber) return 0;
    const str = String(unitNumber).toUpperCase().trim();
    
    // Handle penthouse (PH) - put at top (floor 999)
    if (str.startsWith('PH')) {
      const num = parseInt(str.replace(/\D/g, ''), 10);
      return isNaN(num) ? 999 : 900 + num;
    }
    
    // Extract numeric part
    const numericPart = str.replace(/\D/g, '');
    if (!numericPart) return 0;
    
    const len = numericPart.length;
    if (len >= 4) {
      // First 2 digits are floor
      return parseInt(numericPart.slice(0, 2), 10);
    } else if (len === 3) {
      // First digit is floor
      return parseInt(numericPart.slice(0, 1), 10);
    } else if (len === 2) {
      // Could be floor + unit, treat first digit as floor
      return parseInt(numericPart.slice(0, 1), 10);
    }
    return parseInt(numericPart, 10) || 0;
  };

  // Sort units by floor (descending - highest floor first)
  const sortUnitsByFloor = (units) => {
    return [...units].sort((a, b) => {
      const floorA = getFloorFromUnit(a.unit_number);
      const floorB = getFloorFromUnit(b.unit_number);
      // Sort descending (highest floor first)
      return floorB - floorA;
    });
  };

  // Group units by stack - use unitsData (full data) for health scores and units list
  const stackGroups = useMemo(() => {
    const groups = {};
    
    // Build groups from full unitsData (not filtered)
    unitsData.forEach(unit => {
      const stackNum = unit.stack_number || 'Unknown';
      if (!groups[stackNum]) {
        groups[stackNum] = {
          stackNumber: stackNum,
          units: [],
          totalUnits: 0,
          okCount: 0,
          issueCount: 0,
        };
      }
      groups[stackNum].units.push(unit);
      groups[stackNum].totalUnits++;
      if (unit.has_issues) {
        groups[stackNum].issueCount++;
      } else {
        groups[stackNum].okCount++;
      }
    });

    // Calculate health percentage and sort units within each stack
    Object.values(groups).forEach(group => {
      group.healthPercent = group.totalUnits > 0 
        ? Math.round((group.okCount / group.totalUnits) * 100) 
        : 100;
      // Sort units by floor (highest first)
      group.units = sortUnitsByFloor(group.units);
    });

    return Object.values(groups);
  }, [unitsData]);

  // Sorted stack groups based on current sort settings
  const sortedStackGroups = useMemo(() => {
    const sorted = [...stackGroups];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      if (sortColumn === 'stack') {
        const numA = parseInt(a.stackNumber, 10);
        const numB = parseInt(b.stackNumber, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          comparison = numA - numB;
        } else {
          comparison = String(a.stackNumber).localeCompare(String(b.stackNumber));
        }
      } else if (sortColumn === 'health') {
        comparison = a.healthPercent - b.healthPercent;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [stackGroups, sortColumn, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <FaSort style={{ marginLeft: '4px', opacity: 0.3, fontSize: '0.7rem' }} />;
    }
    return sortDirection === 'asc' 
      ? <FaSortUp style={{ marginLeft: '4px', fontSize: '0.7rem' }} />
      : <FaSortDown style={{ marginLeft: '4px', fontSize: '0.7rem' }} />;
  };

  const toggleStack = (stackNumber, group) => {
    const currentlyExpanded = expandedStacks[stackNumber] || (shouldAutoExpand(group) && !manuallyCollapsed[stackNumber]);
    
    if (currentlyExpanded) {
      // Collapsing - if it was auto-expanded by search, mark as manually collapsed
      if (shouldAutoExpand(group)) {
        setManuallyCollapsed(prev => ({ ...prev, [stackNumber]: true }));
      }
      setExpandedStacks(prev => {
        const newExpanded = { ...prev, [stackNumber]: false };
        if (onExpandedStacksChange) {
          const expandedList = Object.keys(newExpanded).filter(key => newExpanded[key]);
          onExpandedStacksChange(expandedList);
        }
        return newExpanded;
      });
    } else {
      // Expanding - clear manually collapsed flag
      setManuallyCollapsed(prev => ({ ...prev, [stackNumber]: false }));
      setExpandedStacks(prev => {
        const newExpanded = { ...prev, [stackNumber]: true };
        if (onExpandedStacksChange) {
          const expandedList = Object.keys(newExpanded).filter(key => newExpanded[key]);
          onExpandedStacksChange(expandedList);
        }
        return newExpanded;
      });
    }
  };

  // Check if a unit matches the search term
  const isUnitSearchMatch = (unit) => {
    if (!searchTerm || searchTerm.trim() === '') return false;
    const searchLower = searchTerm.toLowerCase().trim();
    const unitNum = (unit.unit_number || '').toLowerCase();
    return unitNum.includes(searchLower);
  };

  // Check if a stack should be auto-expanded due to search match
  const shouldAutoExpand = (group) => {
    if (!searchTerm || searchTerm.trim() === '') return false;
    // Check if any unit in this stack has a direct match on unit_number
    return group.units.some(unit => isUnitSearchMatch(unit));
  };

  // Determine if a stack should be shown as expanded (either manually or auto)
  const isStackExpanded = (group) => {
    // If manually collapsed during search, respect that
    if (manuallyCollapsed[group.stackNumber]) return false;
    return expandedStacks[group.stackNumber] || shouldAutoExpand(group);
  };
  
  // Clear manually collapsed state when search term changes
  React.useEffect(() => {
    setManuallyCollapsed({});
  }, [searchTerm]);

  // Check if a unit should be highlighted (hovered or search match)
  const isUnitHighlighted = (unit) => {
    return hoveredUnitId === unit.objectId || isUnitSearchMatch(unit);
  };

  const handleOpenReport = (row) => {
    setSelectedRow(row);
    setShowReportModal(true);
  };

  const handleCloseReport = () => {
    setShowReportModal(false);
    setSelectedRow(null);
  };

  const handleOpenCarousel = (photos, index, title) => {
    setCarouselPhotos(photos || []);
    setCarouselIndex(index);
    setCarouselTitle(title);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSelectCarousel = (selectedIndex) => {
    setCarouselIndex(selectedIndex);
  };

  // Color palette for stack badges - visually distinct colors
  const STACK_COLORS = [
    { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' }, // Blue
    { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4' }, // Pink
    { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' }, // Green
    { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' }, // Amber
    { bg: '#e0e7ff', color: '#3730a3', border: '#a5b4fc' }, // Indigo
    { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' }, // Orange
    { bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' }, // Purple
    { bg: '#ccfbf1', color: '#115e59', border: '#5eead4' }, // Teal
    { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' }, // Red
    { bg: '#ecfccb', color: '#3f6212', border: '#bef264' }, // Lime
  ];

  // Hash function to get consistent color index for a stack number
  const getStackColorIndex = (stackNumber) => {
    if (!stackNumber) return 0;
    const str = String(stackNumber);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash) % STACK_COLORS.length;
  };

  // Get color scheme for a stack number
  const getStackColor = (stackNumber) => {
    const index = getStackColorIndex(stackNumber);
    return STACK_COLORS[index];
  };

  // Render a single photo thumbnail with count indicator
  const renderPhotoThumbnail = (photos, title) => {
    if (!photos || photos.length === 0) {
      return <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>—</span>;
    }
    
    const remainingCount = photos.length - 1;
    
    return (
      <div 
        style={{ display: 'flex', gap: '4px', alignItems: 'center' }}
        onClick={(e) => {
          e.stopPropagation();
          handleOpenCarousel(photos, 0, title);
        }}
      >
        <img
          src={photos[0]}
          alt={`${title} 1`}
          style={{
            width: '44px',
            height: '44px',
            objectFit: 'cover',
            borderRadius: '4px',
            cursor: 'pointer',
            border: '1px solid #e5e7eb'
          }}
        />
        {remainingCount > 0 && (
          <span 
            style={{ 
              color: '#666', 
              fontSize: '0.75rem', 
              fontWeight: 600,
              padding: '2px 5px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px'
            }}
          >
            +{remainingCount}
          </span>
        )}
      </div>
    );
  };

  // Render stack badge with consistent color
  const renderStackBadge = (stackNumber) => {
    if (!stackNumber) return <span style={{ color: '#999' }}>—</span>;
    const colors = getStackColor(stackNumber);
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        display: 'inline-block'
      }}>
        {stackNumber}
      </span>
    );
  };

  // Get gradient color based on health percentage (red -> orange -> yellow -> greenyellow -> green)
  const getHealthColor = (percent) => {
    if (percent <= 20) return '#dc2626'; // Red
    if (percent <= 40) return '#ea580c'; // Orange
    if (percent <= 60) return '#eab308'; // Yellow
    if (percent <= 80) return '#84cc16'; // Yellow-green
    return '#16a34a'; // Green
  };

  // Render health bar with solid color based on score
  const renderHealthBar = (healthPercent, okCount, issueCount) => {
    const barColor = getHealthColor(healthPercent);
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ 
          width: '120px', 
          height: '10px', 
          backgroundColor: '#e5e7eb', 
          borderRadius: '5px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${healthPercent}%`,
            height: '100%',
            backgroundColor: barColor,
            borderRadius: '5px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: barColor, minWidth: '40px', whiteSpace: 'nowrap' }}>
          {healthPercent}%
        </span>
      </div>
    );
  };

  // Small icon box component
  const IconBox = ({ children }) => (
    <div style={{ 
      width: '12px', 
      height: '12px', 
      backgroundColor: '#e5e7eb', 
      borderRadius: '2px', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      fontSize: '8px',
      color: '#9ca3af'
    }}>
      {children}
    </div>
  );

  // Report Modal Content Component
  const ReportModalContent = ({ row, onViewPhotos }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageType, setImageType] = useState('before');
    
    const beforePhotos = row.before_photos || [];
    const afterPhotos = row.after_photos || [];
    const allPhotos = imageType === 'before' ? beforePhotos : afterPhotos;
    const currentImage = allPhotos[currentImageIndex];
    
    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % (allPhotos.length || 1));
    };
    
    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + (allPhotos.length || 1)) % (allPhotos.length || 1));
    };

    const switchImageType = (type) => {
      setImageType(type);
      setCurrentImageIndex(0);
    };

    return (
      <div style={{ padding: '0.25rem' }}>
        {/* Header: Unit info with status */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '10px', 
              backgroundColor: '#224982',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: '700',
              fontSize: '1rem'
            }}>
              {row.unit_number || '—'}
            </div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937' }}>
                Unit {row.unit_number}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                Stack {row.stack_number} • {row.condition || 'Not specified'}
              </div>
            </div>
          </div>
          {row.has_issues ? (
            <span style={{
              padding: '6px 14px',
              borderRadius: 999,
              fontSize: '0.85rem',
              fontWeight: 600,
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#dc2626' }}></span>
              Issue Found
            </span>
          ) : (
            <span style={{
              padding: '6px 14px',
              borderRadius: 999,
              fontSize: '0.85rem',
              fontWeight: 600,
              backgroundColor: '#ecfdf5',
              color: '#16a34a',
              border: '1px solid #bbf7d0',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#16a34a' }}></span>
              OK
            </span>
          )}
        </div>

        {/* Main Content: Photos on left, Notes/Issues on right */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem',
          marginBottom: '0.75rem'
        }}>
          {/* Left Side: Image Gallery */}
          <div style={{ 
            width: '260px',
            flexShrink: 0
          }}>
            {(beforePhotos.length > 0 || afterPhotos.length > 0) ? (
              <div style={{ 
                backgroundColor: '#f8fafc',
                borderRadius: '10px',
                padding: '0.625rem',
                border: '1px solid #e2e8f0',
                height: '100%'
              }}>
                {/* Image Type Tabs */}
                <div style={{ 
                  display: 'flex', 
                  gap: '4px', 
                  marginBottom: '0.5rem', 
                  backgroundColor: '#e2e8f0',
                  borderRadius: '6px',
                  padding: '2px'
                }}>
                  <button
                    onClick={() => switchImageType('before')}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '5px',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: imageType === 'before' ? '#fff' : 'transparent',
                      color: imageType === 'before' ? '#1f2937' : '#64748b',
                      boxShadow: imageType === 'before' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    Before ({beforePhotos.length})
                  </button>
                  <button
                    onClick={() => switchImageType('after')}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '5px',
                      border: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: imageType === 'after' ? '#fff' : 'transparent',
                      color: imageType === 'after' ? '#1f2937' : '#64748b',
                      boxShadow: imageType === 'after' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                    }}
                  >
                    After ({afterPhotos.length})
                  </button>
                </div>
                
                {allPhotos.length > 0 ? (
                  <>
                    {/* Main Image */}
                    <div style={{ 
                      position: 'relative',
                      backgroundColor: '#94a3b8',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      height: '220px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: allPhotos.length > 1 ? '0.5rem' : 0
                    }}>
                      {currentImage && (
                        <img 
                          src={currentImage} 
                          alt={`${imageType} photo`}
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain',
                            cursor: 'pointer'
                          }} 
                          onClick={() => onViewPhotos(allPhotos, currentImageIndex, imageType === 'before' ? 'Before' : 'After')}
                        />
                      )}
                      {/* Navigation Arrows */}
                      {allPhotos.length > 1 && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); prevImage(); }}
                            style={{
                              position: 'absolute',
                              left: '6px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              border: 'none',
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              color: '#1f2937',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
                            }}
                          >
                            ‹
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); nextImage(); }}
                            style={{
                              position: 'absolute',
                              right: '6px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '26px',
                              height: '26px',
                              borderRadius: '50%',
                              border: 'none',
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              color: '#1f2937',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
                            }}
                          >
                            ›
                          </button>
                        </>
                      )}
                      {/* Image Counter */}
                      {allPhotos.length > 1 && (
                        <div style={{
                          position: 'absolute',
                          bottom: '6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          color: '#fff',
                          padding: '2px 10px',
                          borderRadius: '10px',
                          fontSize: '0.7rem',
                          fontWeight: 500
                        }}>
                          {currentImageIndex + 1} / {allPhotos.length}
                        </div>
                      )}
                    </div>
                    
                    {/* Thumbnails Row */}
                    {allPhotos.length > 1 && (
                      <div style={{ 
                        display: 'flex', 
                        gap: '6px',
                        overflowX: 'auto'
                      }}>
                        {allPhotos.map((photo, idx) => (
                          <img
                            key={idx}
                            src={photo}
                            alt={`Thumbnail ${idx + 1}`}
                            onClick={() => setCurrentImageIndex(idx)}
                            style={{
                              width: '44px',
                              height: '44px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              border: currentImageIndex === idx ? '2px solid #224982' : '2px solid transparent',
                              opacity: currentImageIndex === idx ? 1 : 0.6,
                              transition: 'all 0.2s ease',
                              flexShrink: 0
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#94a3b8', 
                    padding: '3rem 1rem',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '8px',
                    fontSize: '0.85rem'
                  }}>
                    No {imageType} photos
                  </div>
                )}
              </div>
            ) : (
              <div style={{ 
                backgroundColor: '#f8fafc',
                borderRadius: '10px',
                padding: '3rem 1rem',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: '0.85rem',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                No photos available
              </div>
            )}
          </div>

          {/* Right Side: Notes and Issue Notes */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {/* Notes Card */}
            <div style={{ 
              backgroundColor: '#fff',
              borderRadius: '10px',
              padding: '0.875rem',
              border: '1px solid #e2e8f0',
              flex: 1
            }}>
              <div style={{ 
                fontSize: '0.7rem', 
                fontWeight: 600, 
                color: '#64748b', 
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <FaStickyNote style={{ fontSize: '0.65rem' }} />
                Notes
              </div>
              <div style={{ 
                fontSize: '0.9rem',
                lineHeight: '1.6', 
                color: row.notes ? '#374151' : '#94a3b8',
                whiteSpace: 'pre-wrap',
                fontStyle: row.notes ? 'normal' : 'italic'
              }}>
                {row.notes || 'No additional notes'}
              </div>
            </div>

            {/* Issue Notes - Only shown if there's an issue */}
            {row.has_issues && row.issue_notes && (
              <div style={{ 
                backgroundColor: '#fff',
                borderRadius: '10px',
                padding: '0.875rem',
                border: '1px solid #e2e8f0',
                flex: 1
              }}>
                <div style={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 600, 
                  color: '#dc2626', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <FaExclamationTriangle style={{ fontSize: '0.65rem' }} />
                  Issue Notes
                </div>
                <div style={{ 
                  fontSize: '0.9rem',
                  lineHeight: '1.6', 
                  color: '#374151',
                  whiteSpace: 'pre-wrap'
                }}>
                  {row.issue_notes}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Status Bar */}
        {row.has_issues ? (
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '0.625rem 1rem', 
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.85rem',
            border: '1px solid #fecaca'
          }}>
            <FaExclamationTriangle style={{ fontSize: '0.8rem' }} />
            This issue should be addressed immediately
          </div>
        ) : (
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '0.625rem 1rem', 
            backgroundColor: '#ecfdf5',
            color: '#15803d',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.85rem',
            border: '1px solid #a7f3d0'
          }}>
            <span style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: '#16a34a',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem'
            }}>
              ✓
            </span>
            No issues found during inspection
          </div>
        )}
      </div>
    );
  };

  // Format unit list for display - show 6 units then "..."
  const formatUnitsList = (units) => {
    const unitNumbers = units.map(u => u.unit_number).filter(Boolean).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(a).localeCompare(String(b));
    });
    
    if (unitNumbers.length <= 6) {
      return unitNumbers.join(', ');
    }
    return `${unitNumbers.slice(0, 6).join(', ')}...`;
  };

  return (
    <div className="stack-cleaning-table-wrapper">
      <div className="fancoil-table-container">
        <Card className="shadow-sm border-0 fancoil-table-card">
          <Card.Body style={{ padding: 0 }}>
            <Table hover responsive className="fancoil-table mb-0 border-0" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '65px' }} />
                <col style={{ width: 'auto' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '28px' }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th 
                    onClick={() => handleSort('stack')}
                    style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 600, 
                      padding: '14px 10px 14px 16px', 
                      color: '#374151',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      Stack
                      {renderSortIcon('stack')}
                    </span>
                  </th>
                  <th style={{ fontSize: '0.9rem', fontWeight: 600, padding: '14px 10px', color: '#374151' }}>Units</th>
                  <th 
                    onClick={() => handleSort('health')}
                    style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: 600, 
                      padding: '14px 14px 14px 10px', 
                      color: '#374151',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      Health
                      {renderSortIcon('health')}
                    </span>
                  </th>
                  <th style={{ padding: '14px 16px 14px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedStackGroups.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4" style={{ color: '#999' }}>
                      No stack cleaning records found
                    </td>
                  </tr>
                ) : (
                  sortedStackGroups.map((group) => (
                    <React.Fragment key={group.stackNumber}>
                      {/* Stack Row */}
                      <tr 
                        onClick={() => toggleStack(group.stackNumber, group)}
                        onMouseEnter={() => onHoverStack && onHoverStack(group.stackNumber)}
                        onMouseLeave={() => onHoverStack && onHoverStack(null)}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: hoveredStackId === group.stackNumber ? '#e0f2fe' : isStackExpanded(group) ? '#f0f9ff' : undefined,
                        }}
                        className="stack-row"
                      >
                        <td style={{ verticalAlign: 'middle', padding: '16px 10px 16px 16px' }}>
                          {renderStackBadge(group.stackNumber)}
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '16px 10px', overflow: 'hidden' }}>
                          <span style={{ 
                            color: '#374151', 
                            fontSize: '0.95rem', 
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block'
                          }}>
                            {formatUnitsList(group.units)}
                          </span>
                        </td>
                        <td style={{ verticalAlign: 'middle', padding: '16px 14px 16px 10px' }}>
                          {renderHealthBar(group.healthPercent, group.okCount, group.issueCount)}
                        </td>
                        <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '16px 16px 16px 8px' }}>
                          {isStackExpanded(group) ? (
                            <FaChevronDown style={{ color: '#6b7280', fontSize: '12px' }} />
                          ) : (
                            <FaChevronRight style={{ color: '#6b7280', fontSize: '12px' }} />
                          )}
                        </td>
                      </tr>

                      {/* Expanded Units Table */}
                      {isStackExpanded(group) && (
                        <tr className="expanded-row">
                          <td colSpan="4" style={{ padding: 0, backgroundColor: '#f8fafc' }}>
                            <div style={{ padding: '0.5rem 1rem 1rem 1.5rem' }}>
                              <Table size="sm" className="mb-0 nested-table" style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f8fafc' }}>
                                    <th style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.9rem', width: '80px', color: '#374151' }}>Unit</th>
                                    <th style={{ padding: '12px 10px', fontWeight: 600, fontSize: '0.9rem', width: '140px', color: '#374151' }}>Before/After</th>
                                    <th style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.9rem', color: '#374151' }}>Notes</th>
                                    <th style={{ padding: '12px 14px', fontWeight: 600, fontSize: '0.9rem', width: '100px', color: '#374151' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.units.map((unit) => (
                                    <tr 
                                      key={unit.objectId}
                                      onClick={() => handleOpenReport(unit)}
                                      onMouseEnter={() => onHoverUnit && onHoverUnit(unit.objectId)}
                                      onMouseLeave={() => onHoverUnit && onHoverUnit(null)}
                                      style={{ 
                                        cursor: 'pointer',
                                        backgroundColor: isUnitHighlighted(unit) ? '#e0f2fe' : undefined,
                                        transition: 'background-color 0.15s ease'
                                      }}
                                    >
                                      <td style={{ padding: '14px 14px', fontWeight: 600, fontSize: '0.95rem', width: '80px' }}>
                                        {unit.unit_number || '—'}
                                      </td>
                                      <td style={{ padding: '14px 10px', width: '140px' }}>
                                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                          {/* Before Photo */}
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                            {unit.before_photos && unit.before_photos.length > 0 ? (
                                              <img
                                                src={unit.before_photos[0]}
                                                alt="Before"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOpenCarousel(unit.before_photos, 0, 'Before');
                                                }}
                                                style={{
                                                  width: '44px',
                                                  height: '44px',
                                                  objectFit: 'cover',
                                                  borderRadius: '4px',
                                                  cursor: 'pointer',
                                                  border: '1px solid #e5e7eb'
                                                }}
                                              />
                                            ) : (
                                              <div style={{ width: '44px', height: '44px', backgroundColor: '#f3f4f6', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                                            )}
                                            <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>Before</span>
                                          </div>
                                          {/* After Photo */}
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                            {unit.after_photos && unit.after_photos.length > 0 ? (
                                              <img
                                                src={unit.after_photos[0]}
                                                alt="After"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOpenCarousel(unit.after_photos, 0, 'After');
                                                }}
                                                style={{
                                                  width: '44px',
                                                  height: '44px',
                                                  objectFit: 'cover',
                                                  borderRadius: '4px',
                                                  cursor: 'pointer',
                                                  border: '1px solid #e5e7eb'
                                                }}
                                              />
                                            ) : (
                                              <div style={{ width: '44px', height: '44px', backgroundColor: '#f3f4f6', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                                            )}
                                            <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>After</span>
                                          </div>
                                        </div>
                                      </td>
                                      <td style={{ padding: '14px 14px', color: unit.issue_notes || unit.notes ? '#374151' : '#9ca3af', fontSize: '0.9rem', maxWidth: '300px' }}>
                                        <span style={{ 
                                          display: '-webkit-box', 
                                          WebkitLineClamp: 2, 
                                          WebkitBoxOrient: 'vertical', 
                                          overflow: 'hidden',
                                          lineHeight: '1.4'
                                        }}>
                                          {unit.issue_notes || unit.notes || '—'}
                                        </span>
                                      </td>
                                      <td style={{ padding: '14px 14px', width: '100px' }}>
                                        {unit.has_issues ? (
                                          <span style={{ fontSize: '0.85rem', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#fef2f2', color: '#b91c1c', fontWeight: 600 }}>
                                            Issue
                                          </span>
                                        ) : (
                                          <span style={{ fontSize: '0.85rem', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#ecfdf3', color: '#15803d', fontWeight: 600 }}>
                                            OK
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Photo Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{carouselTitle} Photos</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {carouselPhotos.length > 0 && (
              <Carousel 
                activeIndex={carouselIndex} 
                onSelect={handleSelectCarousel}
                interval={null}
                controls={carouselPhotos.length > 1}
                indicators={carouselPhotos.length > 1}
                wrap={carouselPhotos.length > 1}
              >
                {carouselPhotos.map((photo, idx) => (
                  <Carousel.Item key={idx}>
                    <img
                      src={photo}
                      alt={`${carouselTitle} ${idx + 1}`}
                      className="d-block w-100 carousel-image"
                      style={{ maxHeight: '80vh', objectFit: 'contain', backgroundColor: '#000' }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            )}
          </Modal.Body>
        </Modal>

        {/* View Report Modal */}
        <Modal show={showReportModal} onHide={handleCloseReport} centered size="lg">
          <Modal.Header style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: 'none',
            padding: '1.25rem 1.5rem 0.5rem'
          }}>
            <Modal.Title style={{ 
              fontSize: '1.1rem', 
              fontWeight: 600, 
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#667eea'
              }}></span>
              Inspection Report
            </Modal.Title>
            <button 
              onClick={handleCloseReport} 
              style={{ 
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                fontSize: '1.2rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e2e8f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f1f5f9'}
            >
              ×
            </button>
          </Modal.Header>
          
          <Modal.Body style={{ padding: '0.5rem 1.5rem 1.5rem' }}>
            {selectedRow && (
              <ReportModalContent 
                row={selectedRow} 
                onViewPhotos={(photos, idx, title) => {
                  handleCloseReport();
                  setTimeout(() => handleOpenCarousel(photos, idx, title), 200);
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}

export default StackCleaningTable;
